import React, { useMemo, useState } from 'react';
import { Check, Plus, Sparkles, Trash2, Wand2, X } from 'lucide-react';
import { money, projectDot, ProjectSelect, qty } from './ui';

// Lignes d'une facture et leur répartition sur les projets : une pastille par projet (quantité, montant frais
// compris). Un clic sur une ligne ouvre l'édition (projets et quantités) ; « Tout sur… » affecte toute la facture.

const sameLine = (a, line) => Number(a.line) === Number(line.position);

// Ligne négative (crédit, avoir, remise) : le total de la facture est la somme des lignes, crédits déduits.
const Credit = () => (
  <span className="ml-1 rounded bg-emerald-50 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-emerald-700">crédit</span>
);

const Chip = ({ alloc, currency, onClick }) => {
  const suggestion = alloc.source === 'suggestion';
  return (
    <button
      type="button"
      onClick={onClick}
      title={[alloc.iteration_name && `Itération ${alloc.iteration_name}`, alloc.why, alloc.approximate && 'Réparti au prorata (ancienne facture)']
        .filter(Boolean).join(' · ') || undefined}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-xs transition
        ${suggestion ? 'border border-dashed border-violet-300 bg-violet-50/40 text-gray-700 hover:border-violet-400'
          : 'border border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300'}`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${projectDot(alloc.project_id)}`} />
      <span className="truncate font-medium">{alloc.project_name || `Projet ${alloc.project_id}`}</span>
      <span className="shrink-0 text-gray-500">{qty(alloc.quantity)}</span>
      {alloc.amount !== undefined && <span className="shrink-0 tabular-nums text-gray-500">· {money(alloc.amount, currency)}</span>}
      {suggestion && <Sparkles className="h-3 w-3 shrink-0 text-violet-500" />}
    </button>
  );
};

const LineEditor = ({ line, rows, projects, onCancel, onSave, saving }) => {
  const [draft, setDraft] = useState(rows.length ? rows.map((r) => ({ project_id: r.project_id, quantity: String(r.quantity) }))
    : [{ project_id: null, quantity: String(line.quantity) }]);
  const total = draft.reduce((sum, r) => sum + (Number(String(r.quantity).replace(',', '.')) || 0), 0);
  const left = Math.round((Number(line.quantity) - total) * 1000) / 1000;
  const valid = draft.every((r) => r.project_id && Number(String(r.quantity).replace(',', '.')) > 0) && left >= -1e-6;
  const update = (index, patch) => setDraft((rowsNow) => rowsNow.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  return (
    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50/40 p-2">
      <div className="space-y-1.5">
        {draft.map((row, index) => (
          <div key={index} className="flex items-center gap-2">
            <ProjectSelect projects={projects} value={row.project_id} onChange={(id) => update(index, { project_id: id })}
              className="min-w-0 flex-1" autoFocus={!row.project_id && index === draft.length - 1} />
            <input
              value={row.quantity}
              onChange={(e) => update(index, { quantity: e.target.value })}
              inputMode="decimal"
              className="w-20 rounded-md border border-gray-200 px-2 py-1 text-right text-sm tabular-nums"
              aria-label="Quantité"
            />
            <button type="button" onClick={() => setDraft((r) => r.filter((_, i) => i !== index))}
              className="rounded p-1 text-gray-400 hover:bg-white hover:text-red-600" aria-label="Retirer">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => setDraft((r) => [...r, { project_id: null, quantity: left > 0 ? String(left) : '' }])}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline">
          <Plus className="h-3.5 w-3.5" /> Répartir sur un autre projet
        </button>
        <span className={`text-xs ${left > 1e-6 ? 'text-amber-700' : left < -1e-6 ? 'text-red-600' : 'text-gray-500'}`}>
          {left > 1e-6 ? `Reste ${qty(left)} à affecter` : left < -1e-6 ? `${qty(-left)} de trop` : 'Tout est affecté'}
        </span>
        <div className="flex gap-1">
          <button type="button" onClick={onCancel} className="rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-white">Annuler</button>
          <button type="button" disabled={!valid || saving}
            onClick={() => onSave(draft.map((r) => ({ project_id: r.project_id, quantity: Number(String(r.quantity).replace(',', '.')) })))}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            <Check className="h-3.5 w-3.5" /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const LineAllocations = ({ lines, allocations, unallocated = {}, currency = '€', projects, editable = false, saving = false,
  onChange }) => {
  const [editing, setEditing] = useState(null);
  const [allOn, setAllOn] = useState(null);
  const articles = useMemo(() => lines.filter((l) => l.kind === 'article'), [lines]);
  const fees = useMemo(() => lines.filter((l) => l.kind !== 'article'), [lines]);
  const total = useMemo(() => lines.reduce((sum, l) => sum + (Number(l.total) || 0), 0), [lines]);
  const credits = lines.some((l) => Number(l.total) < 0);

  const save = async (line, rows) => {
    const others = allocations.filter((a) => !sameLine(a, line))
      .map((a) => ({ line: a.line, project_id: a.project_id, iteration_id: a.iteration_id || null, quantity: a.quantity }));
    await onChange([...others, ...rows.map((r) => ({ line: line.position, project_id: r.project_id, quantity: r.quantity }))]);
    setEditing(null);
  };

  const everythingOn = async (projectId) => {
    setAllOn(projectId);
    await onChange(articles.map((l) => ({ line: l.position, project_id: projectId, quantity: l.quantity })));
    setAllOn(null);
  };

  return (
    <div>
      {editable && articles.length > 1 && (
        <div className="mb-2 flex items-center justify-end gap-2 text-xs text-gray-500">
          <Wand2 className="h-3.5 w-3.5" /> Tout sur
          <ProjectSelect projects={projects} value={allOn} onChange={everythingOn} placeholder="un projet…" className="w-48" />
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {articles.map((line) => {
          const rows = allocations.filter((a) => sameLine(a, line));
          const left = unallocated[String(line.position)];
          const ref = [line.mfr_ref || line.mfr_reference, line.ref || line.reference].filter(Boolean)[0];
          return (
            <li key={line.position} className="py-2">
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-gray-800" title={line.designation}>{line.designation}</p>
                  <p className="text-xs text-gray-500">
                    {ref && <span className="mr-2 font-mono text-[11px] text-gray-500">{ref}</span>}
                    <span className="tabular-nums">{qty(line.quantity)} × {money(line.quantity ? line.total / line.quantity : line.total, currency)}</span>
                    <span className="mx-1 text-gray-300">·</span>
                    <span className="tabular-nums font-medium text-gray-700">{money(line.total, currency)}</span>
                    {Number(line.total) < 0 && <Credit />}
                  </p>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-1 sm:max-w-[55%] sm:justify-end">
                  {rows.map((alloc, index) => (
                    <Chip key={`${alloc.project_id}-${index}`} alloc={alloc} currency={currency}
                      onClick={editable ? () => setEditing(line.position) : undefined} />
                  ))}
                  {left > 0 && (
                    <button type="button" disabled={!editable} onClick={() => setEditing(line.position)}
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200 hover:bg-amber-100">
                      <Plus className="h-3 w-3" /> {qty(left)} à affecter
                    </button>
                  )}
                  {!rows.length && !left && <span className="text-xs italic text-gray-400">non réparti</span>}
                </div>
              </div>
              {editing === line.position && (
                <LineEditor line={line} rows={rows} projects={projects} saving={saving}
                  onCancel={() => setEditing(null)} onSave={(r) => save(line, r)} />
              )}
            </li>
          );
        })}
      </ul>
      {fees.length > 0 && (
        <p className="mt-1 flex flex-wrap items-center gap-x-2 border-t border-dashed border-gray-200 pt-2 text-xs text-gray-500">
          {fees.map((f) => (
            <span key={f.position} className="tabular-nums">{f.designation} {money(f.total, currency)}{Number(f.total) < 0 && <Credit />}</span>
          ))}
          <span className="text-gray-400">— ventilés au prorata du prix</span>
        </p>
      )}
      {lines.length > 1 && (
        <p className="mt-2 flex items-baseline justify-between gap-2 border-t border-gray-200 pt-2 text-sm">
          <span className="text-gray-600">Total des lignes{credits ? ' (crédits déduits)' : ''}</span>
          <span className="font-semibold tabular-nums text-gray-900">{money(total, currency)} HT</span>
        </p>
      )}
    </div>
  );
};

export const ProjectTotals = ({ projects, currency }) => (
  <div className="flex flex-wrap gap-1.5">
    {projects.map((p) => (
      <span key={p.project_id} className="inline-flex items-center gap-1.5 rounded-md bg-white px-2 py-1 text-xs ring-1 ring-inset ring-gray-200"
        title={p.iteration_name ? `Itération ${p.iteration_name}` : undefined}>
        <span className={`h-2 w-2 rounded-full ${projectDot(p.project_id)}`} />
        <span className="font-medium text-gray-800">{p.project_name}</span>
        <span className="tabular-nums text-gray-600">{money(p.amount, currency)}</span>
      </span>
    ))}
  </div>
);

export const CloseButton = ({ onClick, label = 'Fermer' }) => (
  <button type="button" onClick={onClick} aria-label={label} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
    <X className="h-4 w-4" />
  </button>
);

export default LineAllocations;
